/**
 * Authentication state management with Zustand
 */

import { create } from 'zustand';
import type { User } from '@/types';
import { getCurrentUser, logout as logoutApi } from '@/services/auth';
import { clearSignedIn, markSignedIn } from '@/utils/sessionHint';

/**
 * Authentication store state
 */
interface AuthState {
  /** Current authenticated user */
  user: User | null;
  /** Loading state for auth operations */
  isLoading: boolean;
  /** Error message if auth operation failed */
  error: string | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
}

/**
 * Authentication store actions
 */
interface AuthActions {
  /** Load current user from API */
  loadUser: () => Promise<void>;
  /** Set user directly (after OAuth callback) */
  setUser: (user: User | null) => void;
  /** Logout and clear user */
  logout: () => Promise<void>;
  /** Clear error message */
  clearError: () => void;
  /** Refresh user session */
  refreshSession: () => Promise<void>;
}

/**
 * Authentication store
 *
 * Manages user authentication state, loading user data,
 * and logout functionality.
 */
export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  // State
  user: null,
  isLoading: true,
  error: null,
  isAuthenticated: false,

  // Actions
  loadUser: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await getCurrentUser();

      if (response.error) {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: response.error.message,
        });
        return;
      }

      if (response.data) markSignedIn();
      set({
        user: response.data || null,
        isAuthenticated: !!response.data,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Failed to load user:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to load user',
      });
    }
  },

  setUser: (user) => {
    if (user) markSignedIn();
    set({
      user,
      isAuthenticated: !!user,
      error: null,
    });
  },

  logout: async () => {
    set({ isLoading: true, error: null });

    try {
      await logoutApi();
      clearSignedIn();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      set({
        isLoading: false,
        error: 'Logout failed',
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  refreshSession: async () => {
    // Same as loadUser but doesn't set loading state
    // Used for silent session refresh
    try {
      const response = await getCurrentUser();

      if (response.error) {
        set({
          user: null,
          isAuthenticated: false,
          error: response.error.message,
        });
        return;
      }

      if (response.data) markSignedIn();
      set({
        user: response.data || null,
        isAuthenticated: !!response.data,
        error: null,
      });
    } catch (error) {
      console.error('Failed to refresh session:', error);
      set({
        user: null,
        isAuthenticated: false,
        error: 'Failed to refresh session',
      });
    }
  },
}));
